import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconShoppingBag, IconPackage, IconBriefcase, IconDroplet, IconLandmark, IconShoppingCart, IconUploadCloud, IconType, IconHash } from './Icons';

const UploadScreen = ({ onComplete, onError, apiBaseUrl = 'http://localhost:5000' }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [projectName, setProjectName] = useState('Мой проект анализа');
  const [industry, setIndustry] = useState('products');
  const [product, setProduct] = useState('');       // продукт опроса ('' = Авто)
  const [productList, setProductList] = useState([]); // список продуктов органолептики

  // Подгружаем список продуктов при выборе органолептики
  useEffect(() => {
    if (industry !== 'organoleptic' || productList.length > 0) return;
    axios.get(`${apiBaseUrl}/api/organoleptic/products`)
      .then(res => {
        if (res.data && res.data.success) setProductList(res.data.products || []);
      })
      .catch(err => console.warn('Failed to load organoleptic products:', err));
  }, [industry, apiBaseUrl, productList.length]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setFileInfo(null);
    setSelectedColumns([]);
    
    if (selectedFile) {
      analyzeFile(selectedFile);
    }
  };

  const analyzeFile = async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('Uploading file to:', `${apiBaseUrl}/api/upload`);
      const response = await axios.post(`${apiBaseUrl}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('File analysis response:', response.data);
      
      if (response.data.success) {
        // Цель Яндекс.Метрики: файл загружен и проанализирован
        if (window.ym) window.ym(111057568, 'reachGoal', 'FILE_UPLOAD');
        setFileInfo(response.data);
      } else {
        onError(response.data.error || 'Ошибка при анализе файла');
      }
    } catch (err) {
      console.error('Upload error:', err);
      let errorMessage = 'Ошибка при анализе файла';
      
      if (err.response) {
        errorMessage = err.response.data?.error || `Ошибка сервера: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'Нет ответа от сервера. Проверьте:\n1. Запущен ли бэкенд на localhost:5000?\n2. Нет ли блокировки CORS?';
      }
      
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (columnName) => {
    setSelectedColumns(prev => 
      prev.includes(columnName)
        ? prev.filter(col => col !== columnName)
        : [...prev, columnName]
    );
  };

  const handleCreateProject = async () => {
    if (!file || selectedColumns.length === 0) {
      onError('Выберите файл и хотя бы одну колонку для анализа');
      return;
    }

    setLoading(true);
    
    try {
      // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ ДЛЯ РУССКОГО ЯЗЫКА:
      // Используем данные, которые уже были корректно прочитаны бэкендом,
      // вместо того чтобы пытаться перечитать файл в браузере (что часто ломает кодировку)
      let texts = [];
      let respondents = null; // Инициализируем переменную для двухэтапной кластеризации
      
      if (fileInfo && fileInfo.full_data) {
        console.log('Using full_data from backend analysis...');
        console.log('Selected columns:', selectedColumns);
        console.log('Total rows in file:', fileInfo.full_data.length);
              
        // ДВУХЭТАПНАЯ КЛАСТЕРИЗАЦИЯ: Сохраняем структуру респондентов
        // Этап 1: Классифицируем каждую колонку отдельно
        // Этап 2: Определяем доминирующий кластер для респондента
        
        respondents = fileInfo.full_data.map((row, idx) => {
          const responses = selectedColumns.map(colName => {
            const value = row[colName];
            return (value !== null && value !== undefined && value !== '' && String(value).trim() !== '') 
              ? String(value).trim() 
              : null;
          }).filter(v => v !== null);
          
          // Получаем исходный ID респондента из колонки 'id' или первой числовой колонки
          const allKeys = Object.keys(row);
          
          // Ищем колонку с названием 'id' (регистронезависимо)
          let idKey = allKeys.find(k => k.toLowerCase() === 'id');
          
          // Если не нашли 'id', ищем колонку с числовыми значениями в первой строке
          if (!idKey) {
            for (const key of allKeys) {
              const val = row[key];
              if (typeof val === 'number' || (typeof val === 'string' && val.trim().match(/^\d+$/))) {
                idKey = key;
                break;
              }
            }
          }
          
          // Если всё ещё не нашли, используем первую колонку
          if (!idKey && allKeys.length > 0) {
            idKey = allKeys[0];
          }
          
          const idValue = idKey ? row[idKey] : null;
          // Проверяем что значение есть (строка или число)
          const originalId = (idValue !== undefined && idValue !== null && idValue !== '') 
            ? idValue 
            : (idx + 1);
          
          return {
            respondent_id: originalId,
            responses: responses
          };
        }).filter(r => r.responses.length > 0); // Только респонденты с хотя бы одним ответом
        
        // Собираем все тексты для первичной классификации
        texts = [];
        respondents.forEach(r => {
          r.responses.forEach(text => texts.push(text));
        });
              
        console.log('Two-stage clustering preparation:');
        console.log('  - Total respondents:', respondents.length);
        console.log('  - Total individual responses:', texts.length);
        console.log('  - Columns:', selectedColumns.join(', '));
        console.log('  - Sample respondent #1:', respondents[0]);
      } else {
        console.warn('Full data not found in fileInfo, falling back to local extraction');
        texts = await extractTextsFromFile(file, selectedColumns);
      }
      
      console.log('Creating project at:', `${apiBaseUrl}/api/create_project`);
      console.log('With data:', {
        project_name: projectName,
        texts_count: texts.length,
        columns: selectedColumns,
        has_respondents: !!respondents
      });
      
      const response = await axios.post(`${apiBaseUrl}/api/create_project`, {
        project_name: projectName,
        texts: texts,
        columns: selectedColumns,
        industry: industry,
        product: industry === 'organoleptic' ? (product || null) : null,
        respondents: respondents
      });

      console.log('Create project response:', response.data);
      
      if (response.data.success) {
        // Цель Яндекс.Метрики: кластеризация выполнена
        if (window.ym) window.ym(111057568, 'reachGoal', 'CLUSTERING_DONE');
        onComplete(response.data);
      } else {
        onError(response.data.error || 'Ошибка при создании проекта');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      let errorMsg = 'Ошибка при создании проекта';
      
      if (err.response) {
        errorMsg = err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMsg = 'Нет ответа от сервера. Проверьте подключение.';
      }
      
      onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const extractTextsFromFile = (file, columns) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          console.log('File content loaded, length:', content.length);
          
          const lines = content.split('\n')
            .filter(line => line.trim() !== '');
          
          if (lines.length <= 1) {
            console.log('File has no data rows');
            resolve([]);
            return;
          }
          
          // Парсим заголовок
          const headerLine = lines[0];
          const headers = headerLine.split(',').map(h => h.trim().replace(/"/g, ''));
          
          // Находим индексы выбранных колонок
          const columnIndices = columns.map(colName => {
            const index = headers.indexOf(colName);
            return index >= 0 ? index : 0;
          });
          
          console.log('Column indices:', columnIndices, 'for columns:', columns);
          
          // Пропускаем заголовок
          const dataLines = lines.slice(1);
          
          // Собираем каждый ответ из каждой колонки ОТДЕЛЬНО (независимая классификация)
          const texts = [];
          columnIndices.forEach((colIdx, idx) => {
            const colName = columns[idx];
            console.log(`Extracting column: ${colName} (index ${colIdx})`);
            let colCount = 0;
            
            dataLines.forEach(line => {
              const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
              const value = parts[colIdx] || '';
              
              if (value.length > 0) {
                texts.push(value);
                colCount++;
              }
            });
            
            console.log(`  - Extracted ${colCount} texts from ${colName}`);
          });
          
          console.log('Extracted texts from', columns.length, 'columns:', texts.length, 'texts');
          
          if (texts.length === 0) {
            // Запасной вариант: тестовые данные
            resolve([
              'Что такое Python и для чего он используется?',
              'Как установить Python на Windows?',
              'Основные особенности языка Python',
              'Что такое React и его преимущества?',
              'Как создать компонент в React?',
              'Основные концепции React'
            ]);
          } else {
            resolve(texts);
          }
          
        } catch (error) {
          console.error('Error extracting texts:', error);
          // Возвращаем тестовые данные
          resolve([
            'Пример текста для кластеризации',
            'Другой текст для анализа',
            'Еще один пример текста'
          ]);
        }
      };
      
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-8');
    });
  };

  // Остальная часть компонента (JSX) остается без изменений
  return (
    <div className="upload-screen">
      <div className="upload-card">
        <h2>1. Загрузка данных</h2>
        
        <form className="project-name-input" onSubmit={(e) => { e.preventDefault(); if (file && selectedColumns.length > 0) handleCreateProject(); }}>
          <label>Название проекта:</label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (file && selectedColumns.length > 0) handleCreateProject(); }}}
            placeholder="Введите название проекта"
          />
        </form>

        <div className="industry-selector" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Тип анализируемых данных (отрасль):</label>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'products' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'products' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="products"
                checked={industry === 'products'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconShoppingBag size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Продукты питания (Food)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Вкус, упаковка, наличие в магазинах и на полках, цена, качество, бренд и пр.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'non_food' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'non_food' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="non_food"
                checked={industry === 'non_food'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconPackage size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Товары (Non-Food)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Уход за телом, уход за домом, уход за автомобилем, состав, цена, качество и пр.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'services' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'services' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="services"
                checked={industry === 'services'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconBriefcase size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Услуги (Services)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Персонал, скорость, цена и качество обслуживания, решение проблем, поддержка и пр.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'organoleptic' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'organoleptic' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="organoleptic"
                checked={industry === 'organoleptic'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconDroplet size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Органолептика (Organoleptic)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Вкус, аромат, текстура, температура. Для продуктовых тестов и дегустаций.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'banks' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'banks' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="banks"
                checked={industry === 'banks'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconLandmark size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Банки и финансы (Banks)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Персонал, мобильное приложение, тарифы и комиссии, кредиты, вклады, карты, отделения, поддержка, кэшбэк и пр.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', flex: '1 1 calc(33.333% - 10px)', minWidth: '200px', backgroundColor: industry === 'generic' ? 'rgba(0, 123, 255, 0.18)' : 'var(--panel-2)', borderColor: industry === 'generic' ? '#007bff' : 'var(--border)' }}>
              <input
                type="radio"
                name="industry"
                value="generic"
                checked={industry === 'generic'}
                onChange={(e) => setIndustry(e.target.value)}
                style={{ marginRight: '10px' }}
              />
              <div>
                <div style={{ fontWeight: 'bold' }}><IconShoppingCart size={15} style={{ marginRight: '6px', marginTop: '-2px' }} />Ритейл и маркетплейсы (Retail)</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Доставка, соответствие описанию, возвраты, ПВЗ и др. ИИ создаст категории автоматически.</div>
              </div>
            </label>
          </div>
        </div>

        {industry === 'organoleptic' && (
          <div className="product-selector" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Продукт опроса (контекст вкуса):
            </label>
            <select
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              style={{ width: '100%', maxWidth: '420px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px' }}
            >
              <option value="">Определять автоматически (рекомендуется)</option>
              {productList.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
              Укажите продукт, если весь опрос про один продукт, а респонденты не называют его в ответах
              (пишут просто «кислый», «с металлическим привкусом»). Это помогает правильно различать похвалу и дефект.
            </div>
          </div>
        )}

        <div className="file-upload-section">
          <div className="upload-area">
            <label className="file-upload-label">
              <div className="upload-icon"><IconUploadCloud size={44} /></div>
              <div className="upload-text">
                {file ? file.name : 'Перетащите или выберите файл'}
              </div>
              <div className="upload-subtext">
                Поддерживаемые форматы: CSV, Excel (.xlsx, .xls)
              </div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="file-input"
              />
            </label>
          </div>
        </div>

        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            Анализ файла...
          </div>
        )}

        {fileInfo && (
          <div className="file-analysis">
            <h3>Анализ файла:</h3>
            <div className="file-stats">
              <div className="stat">
                <span className="stat-label">Строк:</span>
                <span className="stat-value">{fileInfo.row_count || 0}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Колонок:</span>
                <span className="stat-value">
                  {fileInfo.columns ? fileInfo.columns.length : 0}
                </span>
              </div>
            </div>

            <h4>Выберите колонки для анализа:</h4>
            <div className="columns-list">
              {fileInfo.columns && fileInfo.columns.map((col, index) => (
                <div key={index} className="column-item">
                  <label className="column-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(col.name)}
                      onChange={() => toggleColumn(col.name)}
                    />
                    <div className="column-info">
                      <div className="column-name">{col.name}</div>
                      <div className="column-details">
                        <span className={`column-type ${col.type}`}>
                          {col.type === 'text' ? (<><IconType size={12} style={{ marginRight: '4px', marginTop: '-2px' }} />Текст</>) : (<><IconHash size={12} style={{ marginRight: '4px', marginTop: '-2px' }} />Числа</>)}
                        </span>
                        <span className="column-count">
                          {col.non_null_count || 0} значений
                        </span>
                      </div>
                      {col.type === 'text' && col.sample_values && col.sample_values.length > 0 && (
                        <div className="column-samples">
                          Примеры: {col.sample_values.join(', ')}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>

            <div className="selected-summary">
              Выбрано колонок: {selectedColumns.length}
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button
            className="primary-button"
            onClick={handleCreateProject}
            disabled={loading || !file || selectedColumns.length === 0}
          >
            {loading ? 'Обработка...' : 'Создать проект и продолжить →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadScreen;
